import React, { useEffect, useState, useRef } from 'react';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Sun, Lightbulb, Loader2, Upload, Download, Sparkles,
  ImageIcon, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RelightStudioPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const fileInputRef = useRef(null);

  const [presets, setPresets] = useState([]);
  const [activePresetId, setActivePresetId] = useState('golden_hour');
  const [sourceFile, setSourceFile] = useState(null);
  const [sourcePreview, setSourcePreview] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [outputUrl, setOutputUrl] = useState(null);

  useEffect(() => {
    axios.get(`${API}/relight/presets`).then(r => setPresets(r.data?.lighting_presets || [])).catch(() => {});
  }, []);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setSourceFile(f);
    setSourcePreview(URL.createObjectURL(f));
    setOutputUrl(null);
    setJobId(null);
    setStatus(null);
  };

  const handleRelight = async () => {
    if (!sourceFile) return toast.error('Upload a portrait first');
    setGenerating(true);
    setOutputUrl(null);
    setStatus('uploading');
    try {
      const fd = new FormData();
      fd.append('source', sourceFile);
      fd.append('preset_id', activePresetId);
      fd.append('generation_type', 'image');
      fd.append('resolution', '1080p');
      if (prompt.trim()) fd.append('prompt', prompt.trim());

      const { data } = await axios.post(`${API}/relight/start`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setJobId(data.job_id);
      setStatus(data.status || 'completed');
      if (data.output_url) {
        setOutputUrl(data.output_url);
        setGenerating(false);
        toast.success('Relit portrait ready');
      } else {
        toast.info('Relight running...');
        poll(data.job_id);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(msg || 'Relight failed');
      setGenerating(false);
    }
  };

  const poll = (id) => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      if (attempts > 90) {
        clearInterval(interval);
        setGenerating(false);
        toast.error('Relight timed out');
        return;
      }
      try {
        const { data } = await axios.get(`${API}/relight/status/${id}`, { headers });
        setStatus(data.status);
        if (data.output_url) {
          setOutputUrl(data.output_url);
          clearInterval(interval);
          setGenerating(false);
          toast.success('Relight complete');
        } else if (data.status === 'failed') {
          clearInterval(interval);
          setGenerating(false);
          toast.error(data.error || 'Relight failed');
        }
      } catch (e) {
        // continue polling
      }
    }, 4000);
  };

  const activePreset = presets.find(p => p.id === activePresetId);

  return (
    <AppShell>
      <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="relight-studio-page">
        {/* Header */}
        <div className="border-b border-white/[0.06] bg-[#0d0d14]/80 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Sun className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="relight-title">
                  Relight Studio
                  <span className="text-[10px] uppercase tracking-wider text-orange-400 bg-orange-500/10 border border-orange-500/20 px-2 py-0.5 rounded-md">Delphi · Relight</span>
                </h1>
                <p className="text-sm text-white/40">Cinematic AI relighting for portraits and scenes — physics-aware lighting transfer at 1080p</p>
              </div>
            </div>
            <Button onClick={handleRelight} disabled={generating || !sourceFile} data-testid="relight-run-btn"
              className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white border-0">
              {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Relighting</> : <><Sparkles className="w-4 h-4 mr-2" />Relight</>}
            </Button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: source + presets */}
          <div className="space-y-6 lg:col-span-1">
            {/* Source upload */}
            <section className="rounded-xl border border-white/[0.06] bg-[#12121a]/60 p-5">
              <label className="text-xs uppercase tracking-wider text-white/40 mb-3 block">Source Portrait</label>
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFileChange} className="hidden" data-testid="relight-source-input" />
              {sourcePreview ? (
                <div className="relative group">
                  <img src={sourcePreview} alt="source" className="w-full aspect-[3/4] object-cover rounded-lg border border-white/10" />
                  <button onClick={() => fileInputRef.current?.click()} data-testid="relight-source-change"
                    className="absolute inset-0 bg-black/0 hover:bg-black/60 transition-all rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <span className="text-xs uppercase tracking-wider text-white/90 flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" />Change</span>
                  </button>
                </div>
              ) : (
                <button onClick={() => fileInputRef.current?.click()} data-testid="relight-source-upload"
                  className="w-full aspect-[3/4] rounded-lg border-2 border-dashed border-white/10 hover:border-orange-500/40 hover:bg-orange-500/[0.03] transition-all flex flex-col items-center justify-center gap-2 text-white/40">
                  <Upload className="w-7 h-7" />
                  <span className="text-sm">Drop or click to upload</span>
                  <span className="text-[10px] uppercase tracking-wider">PNG · JPG · WebP</span>
                </button>
              )}
            </section>

            {/* Optional prompt */}
            <section className="rounded-xl border border-white/[0.06] bg-[#12121a]/60 p-5">
              <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block">Refine Prompt (Optional)</label>
              <Textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="e.g. add subtle teal shadows in the background"
                data-testid="relight-prompt"
                className="bg-black/40 border-white/[0.06] text-white min-h-[80px] resize-none text-sm" />
              <p className="text-[10px] text-white/30 mt-2">Leave blank to use the preset&apos;s default prompt.</p>
            </section>
          </div>

          {/* Center: preset picker */}
          <div className="lg:col-span-1">
            <section className="rounded-xl border border-white/[0.06] bg-[#12121a]/60 p-5">
              <label className="text-xs uppercase tracking-wider text-white/40 mb-3 flex items-center gap-2"><Lightbulb className="w-3 h-3" />Lighting Preset</label>
              <div className="grid grid-cols-2 gap-2" data-testid="relight-presets">
                {presets.map(p => (
                  <button key={p.id} onClick={() => setActivePresetId(p.id)} data-testid={`preset-${p.id}`}
                    className={`text-left rounded-lg overflow-hidden border transition-all duration-200 ${
                      activePresetId === p.id
                        ? 'border-orange-500/60 ring-2 ring-orange-500/20 shadow-[0_0_30px_-10px_rgba(249,115,22,0.5)]'
                        : 'border-white/[0.06] hover:border-white/20'
                    }`}>
                    <div className="aspect-square relative">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${p.reference_url}`} alt={p.name} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      <div className="absolute bottom-1.5 left-2 right-2">
                        <p className="text-[11px] font-semibold text-white truncate">{p.name}</p>
                      </div>
                      {activePresetId === p.id && (
                        <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-orange-500 flex items-center justify-center">
                          <Sparkles className="w-2.5 h-2.5 text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              {activePreset && (
                <div className="mt-4 p-3 bg-black/40 rounded-md border border-white/[0.04]">
                  <p className="text-[10px] uppercase tracking-wider text-orange-300/70 mb-1">{activePreset.name}</p>
                  <p className="text-[12px] text-white/70 leading-snug">{activePreset.prompt}</p>
                </div>
              )}
            </section>
          </div>

          {/* Right: result */}
          <div className="lg:col-span-1">
            <section className="rounded-xl border border-white/[0.06] bg-[#12121a]/60 p-5 h-full flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs uppercase tracking-wider text-white/40">Relit Output</label>
                {status && (
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
                    outputUrl ? 'bg-emerald-500/10 text-emerald-400' :
                    status === 'failed' ? 'bg-red-500/10 text-red-400' :
                    'bg-orange-500/10 text-orange-400'
                  }`} data-testid="relight-status">{status}</span>
                )}
              </div>
              <div className="flex-1 flex items-center justify-center min-h-[400px]">
                {generating && !outputUrl && (
                  <div className="text-center" data-testid="relight-processing">
                    <Loader2 className="w-8 h-8 text-orange-400 animate-spin mx-auto mb-3" />
                    <p className="text-sm text-white/60">Relighting your portrait...</p>
                    <p className="text-[11px] text-white/30 mt-1">~10-30 seconds at 1080p</p>
                  </div>
                )}
                {!generating && outputUrl && (
                  <div className="w-full space-y-3" data-testid="relight-result">
                    <img src={outputUrl} alt="relit" className="w-full aspect-[3/4] object-cover rounded-lg border border-orange-500/30 shadow-[0_0_40px_-10px_rgba(249,115,22,0.4)]" data-testid="relight-image" />
                    <Button onClick={() => { const a = document.createElement('a'); a.href = outputUrl; a.target = '_blank'; a.download = 'relit.png'; a.click(); }}
                      data-testid="relight-download" variant="outline" className="w-full border-white/10 text-white/80 hover:bg-white/5">
                      <Download className="w-3.5 h-3.5 mr-2" />Download
                    </Button>
                  </div>
                )}
                {!generating && !outputUrl && (
                  <div className="text-center text-white/30" data-testid="relight-empty">
                    <ImageIcon className="w-10 h-10 mx-auto mb-2" />
                    <p className="text-sm">Awaiting relight</p>
                    <p className="text-[11px] mt-1">Upload a portrait and pick a preset</p>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>

        {/* How it works strip */}
        <div className="max-w-7xl mx-auto px-6 pb-12">
          <div className="rounded-xl border border-white/[0.06] bg-[#0d0d14] p-5">
            <p className="text-xs uppercase tracking-[0.3em] text-orange-300/60 font-semibold mb-3 flex items-center gap-2">
              <Sparkles className="w-3 h-3" />Powered by Nano Banana
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[13px]">
              <div className="flex items-start gap-3">
                <span className="text-orange-400 font-mono">01</span>
                <p className="text-white/60">Upload any portrait — Delphi preserves identity, pose, and clothing exactly.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-orange-400 font-mono">02</span>
                <p className="text-white/60">Pick a curated lighting preset or refine with a custom prompt.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-orange-400 font-mono">03</span>
                <p className="text-white/60">Cinematic relit result rendered at 1080p in seconds — free on every paid plan.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
